// Shared date and text normalizers

const str = (value) =>
  value == null || value === "" ? "" : String(value).trim();

export const GENDER_LABELED_PATTERN =
  /gender\s*[:-]?\s*(male|female|m\.?|f\.?|gander|gnder|sex)\b/i;

const OCR_MONTH_TO_NUM = {
  jan: "01",
  feb: "02",
  mar: "03",
  apr: "04",
  may: "05",
  jun: "06",
  jul: "07",
  aug: "08",
  sep: "09",
  oct: "10",
  nov: "11",
  dec: "12",
};

const monthNameToNum = (name) => {
  const key = String(name || "").toLowerCase().slice(0, 3);
  return OCR_MONTH_TO_NUM[key] || "";
};

export const parseOcrDateParts = (dateStr) => {
  const value = str(dateStr);
  if (!value) return null;

  const slashMatch = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slashMatch) {
    const [, day, month, year] = slashMatch;
    return { year, month: month.padStart(2, "0"), day: day.padStart(2, "0") };
  }

  const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return { year, month, day };
  }

  const dmyMatch = value.match(/^(\d{1,2})\s+([A-Za-z]{3,9})\s+(\d{4})$/);
  if (dmyMatch) {
    const [, day, monthName, year] = dmyMatch;
    const month = monthNameToNum(monthName);
    if (month) return { year, month, day: day.padStart(2, "0") };
  }

  const compactMatch = value.match(/^(\d{2})([A-Za-z]{3})(\d{4})$/i);
  if (compactMatch) {
    const [, day, monthName, year] = compactMatch;
    const month = monthNameToNum(monthName);
    if (month) return { year, month, day };
  }

  const dashMatch = value.match(/^(\d{1,2})[-.](\d{1,2})[-.](\d{4})$/);
  if (dashMatch) {
    const [, day, month, year] = dashMatch;
    return { year, month: month.padStart(2, "0"), day: day.padStart(2, "0") };
  }

  return null;
};

export const formatOcrDate = (dateString) => {
  const parts = parseOcrDateParts(dateString);
  if (!parts) return str(dateString);
  return `${parts.day}/${parts.month}/${parts.year}`;
};

export const sanitizeCnicInput = (value) => {
  if (value == null || value === "") return "";
  const cleaned = String(value).replace(/[^\d-]/g, "");
  let digits = 0;
  let result = "";
  for (const ch of cleaned) {
    if (ch === "-") {
      result += ch;
      continue;
    }
    if (digits >= 13) continue;
    result += ch;
    digits += 1;
  }
  return result;
};

export const normalizeGender = (value) => {
  if (!value) return "";
  const g = String(value).toLowerCase().replace(/\./g, "").trim();
  if (!g) return "";
  if (g === "m" || g === "male" || g.startsWith("m")) return "Male";
  if (g === "f" || g === "female" || g.startsWith("f")) return "Female";
  if (g === "other" || g === "o") return "Other";
  return "";
};

const isDateLikeLine = (line) =>
  /\d{1,2}[./-]\d{1,2}[./-]\d{2,4}/.test(line) ||
  /\d{1,2}\s+[A-Za-z]{3,9}\s+\d{4}/.test(line);

const extractGenderFromIdCardText = (text, cnicNo) => {
  const labeled = text.match(GENDER_LABELED_PATTERN);
  if (labeled?.[1]) {
    const normalized = normalizeGender(labeled[1]);
    if (normalized) return normalized;
  }

  if (/\bfemale\b/i.test(text) && !/\bmale\b/i.test(text)) return "Female";
  if (/\bmale\b/i.test(text)) return "Male";

  if (cnicNo) {
    const last = cnicNo.replace(/\D/g, "").slice(-1);
    const digit = Number(last);
    if (!Number.isNaN(digit)) {
      return digit % 2 === 1 ? "Male" : "Female";
    }
  }

  return null;
};

export const inferGenderFromOcrText = (text, cnicNo = "") => {
  const fromPatterns = extractGenderFromIdCardText(text, cnicNo);
  if (fromPatterns) return fromPatterns;

  const lines = String(text || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  const sexLineIndex = lines.findIndex((line) =>
    /\b(?:\d+\.\s*)?(?:sex|gender)\b/i.test(line),
  );

  if (sexLineIndex >= 0) {
    for (
      let i = sexLineIndex + 1;
      i < Math.min(sexLineIndex + 6, lines.length);
      i += 1
    ) {
      const line = lines[i];
      if (isDateLikeLine(line)) continue;
      if (/^(m|f)$/i.test(line)) return normalizeGender(line);
      if (/^(male|female|other)$/i.test(line)) return normalizeGender(line);
    }
  }

  return "";
};
